/**
 * Full SSR (treatment) entry for the model detail route. Mounted via
 * `dynamicWithExperiment` from
 * `routes/_app.model.$modelIdSlug.($versionId).tsx` whenever the
 * global `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment resolves to
 * `treatment`.
 *
 * Three render paths, in priority order:
 * 1. Hard error (`ssrLoadError`, e.g. invalid route param, missing
 *    version) → `<AppErrorState />`.
 * 2. SSR seed available (`modelApollo`) → route-scoped Apollo client
 *    pinned by **modelId only** so version-tab pill clicks
 *    (`/model/:id` → `/model/:id/:vId`) don't tear down the cache;
 *    freshly-loaded version documents are written into the cached
 *    client via `versionWriteRef` to avoid `loading: true` blinks on
 *    `useGetGenerationModelByVersionId`.
 * 3. Soft failure (`!modelApollo`, no `ssrLoadError`) → bare
 *    route-scoped Apollo client (no seeds) + `<ModelDetailPage>`,
 *    letting the client-side useQuery hooks re-fetch with the user's
 *    credentials cookie. This keeps logged-in owners able to view
 *    their own private / training-in-progress LoRAs (which anonymous
 *    server-side GraphQL cannot see) without flipping the page into
 *    `<AppErrorState />`. See the comment on the soft-failure branch
 *    below for the full rationale.
 *
 * The CSR (control) entry lives in
 * `_app.model.$modelIdSlug.($versionId).csr.tsx` and ignores all of
 * these branches; it relies on the global `RootRuntime` Apollo
 * client + Apollo hooks for data fetching.
 */
import type { ApolloClient } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useRef } from 'react'
import { useLoaderData, useParams } from 'react-router'
import AppErrorState from 'src/components/AppErrorState'
import { Component as ModelDetailPage } from 'src/pages/ModelDetailPage'
import type { loader } from 'src/routes/_app.model.$modelIdSlug.($versionId)'
import {
  GetGenerationModelByVersionIdDocument,
  GetGenerationModelDocument,
} from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'
import { hasSsrLoaderError } from 'src/utils/ssr-loader-error'

export default function ModelDetailFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const params = useParams()
  const { queryClient } = useRequestContext()
  const ssrLoadError = hasSsrLoaderError(loaderData)
    ? loaderData.ssrLoadError
    : null
  const modelApollo =
    !ssrLoadError && 'modelApollo' in loaderData
      ? loaderData.modelApollo
      : undefined

  // Pin the route-scoped Apollo client by **modelId only** (not by
  // modelId + versionId). Keying on versionId would tear the client
  // down whenever the user clicks one of the version-tab pills (e.g.
  // navigating from `/model/:id` → `/model/:id/:versionId`), which
  // remounts the whole `<ModelDetailPage>` subtree, drops every
  // `useQuery` subscriber back to `loading: true` for a frame, and
  // visibly flashes the cover image. That's the "v1 click → cover
  // shakes" symptom the user reported.
  //
  // Instead: keep the existing client alive across version tab
  // switches and just `client.writeQuery` the freshly-loaded
  // `versionData` into it via the `versionWriteRef` guard below. The
  // live `useGetGenerationModelByVersionId` then hits cache on the
  // very first commit after navigation (`fetchPolicy: 'cache-first'`)
  // — no remount, no flash. Switching to a different model entirely
  // still bumps the key and rebuilds the client, so the previous
  // model's cache doesn't leak.
  const clientRef = useRef<{
    id: string
    client: ApolloClient
  } | null>(null)
  const versionWriteRef = useRef<string | null>(null)
  if (ssrLoadError) {
    return <AppErrorState errorDetail={ssrLoadError.detail} />
  }
  const modelId = modelApollo?.modelId ?? null
  if (modelApollo && modelId && clientRef.current?.id !== modelId) {
    const seeds: Parameters<typeof createRouteApolloClient>[0] = [
      {
        query: GetGenerationModelDocument,
        variables: { id: modelApollo.modelId },
        data: modelApollo.modelData,
      },
    ]
    if (modelApollo.versionId && modelApollo.versionData) {
      seeds.push({
        query: GetGenerationModelByVersionIdDocument,
        variables: { id: modelApollo.versionId },
        data: modelApollo.versionData,
      })
    }
    clientRef.current = { id: modelId, client: createRouteApolloClient(seeds) }
    versionWriteRef.current = modelApollo.versionId ?? null
  }

  // Same model, new version (the v1 / v2 / … pill click). Write the
  // freshly-fetched version document into the cached client so the
  // hook sees it on first render and avoids a `loading: true` blink.
  if (
    modelApollo &&
    modelId &&
    clientRef.current?.id === modelId &&
    modelApollo.versionId &&
    modelApollo.versionData &&
    versionWriteRef.current !== modelApollo.versionId
  ) {
    clientRef.current.client.writeQuery({
      query: GetGenerationModelByVersionIdDocument,
      variables: { id: modelApollo.versionId },
      data: modelApollo.versionData,
    })
    versionWriteRef.current = modelApollo.versionId
  }

  if (modelApollo && clientRef.current?.client) {
    return (
      <QueryClientProvider client={queryClient}>
        <ApolloProvider client={clientRef.current.client}>
          <ModelDetailPage
            initialModel={modelApollo.modelData.generationModel}
            initialVersion={modelApollo.versionData?.generationModelVersion}
          />
        </ApolloProvider>
      </QueryClientProvider>
    )
  }

  // Soft-failure path: SSR loader couldn't get a model (e.g.
  // anonymous server fetch can't see a logged-in owner's private /
  // training-in-progress LoRA). Mount a *bare* route-scoped Apollo
  // client (no seeds) and let `<ModelDetailPage>` itself drive the
  // fetch through Apollo hooks. SSR + first hydrate both render the
  // page's loading state (cache is empty in both), which keeps the
  // DOM structurally identical between the two passes — React
  // happily hydrates without throwing the SSR tree away. Once
  // hydration commits, the client-side useQuery fires through the
  // route client's HttpLink (which already sends
  // `credentials: 'include'`), the user_token cookie reaches the
  // GraphQL server, and the owner gets the same content they'd see
  // under Stage 1 CSR. Anonymous visitors land on the page's
  // existing not-found state instead of the SSR `<AppErrorState/>`.
  //
  // Why not render the CSR entry directly: doing so would mount
  // `<ModelDetailPage>` outside of any `<ApolloProvider>` — the
  // `<RootRuntime>` Apollo provider is bypassed for SSR-shell
  // routes, so Apollo hooks would crash. Building a route-scoped
  // client here is the smallest surface that still keeps the page
  // alive end-to-end.
  //
  // Pin the bare client by `modelIdSlug` so SPA navigations between
  // two soft-failure model URLs still rebuild the cache (avoids
  // serving model A's stale `useGetGenerationModel` result to
  // model B's render).
  const bareKey = `bare:${params.modelIdSlug ?? ''}`
  if (clientRef.current?.id !== bareKey) {
    clientRef.current = { id: bareKey, client: createRouteApolloClient([]) }
    versionWriteRef.current = null
  }
  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={clientRef.current.client}>
        <ModelDetailPage />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
